/**
 * Session persistence for the /rates filter state.
 *
 * WHY: `RatesFilterStateService` is `providedIn: 'root'`, so per-tab filter
 * picks and the active product-type tab survive in-app navigation — but a
 * browser refresh rebuilds the injector and every pick is gone. An analyst who
 * had narrowed the Structured tab to three carriers and two buffer levels
 * landed back on an unfiltered IA grid.
 *
 * WHERE: sessionStorage, not localStorage. Two reasons:
 *   1. Each browser tab gets its own slot, so two /rates tabs open side by side
 *      (a common comparison workflow here) don't overwrite each other.
 *   2. Filters silently hiding rows is only safe while the user remembers
 *      setting them. Surviving a refresh is the ask; surviving until next week
 *      turns into "the grid is missing products" support tickets.
 * Display *preferences* (carrier display, grid font size) stay in localStorage
 * — those are settings, these are a working state.
 *
 * WHAT: everything the rail chrome writes through the service — the per-tab
 * `FilterStateSlot` Records, the per-tab carrier/product selection, the
 * FA-only signals (which live outside the slot), the float-changes switch and
 * the active tab.
 *
 * The parse path is deliberately paranoid. A snapshot written by an older
 * build can be missing fields that every consumer reads unguarded
 * (`slot.selectedStates.length`), so `sanitizeSlot()` merges onto a fresh
 * `emptyFilterStateSlot()` and type-checks each field rather than trusting
 * the JSON. A snapshot that fails any check is discarded whole — a stale
 * filter set is worse than no filter set.
 */

import type { ProductType } from '../models/rate-sheet.models';
import {
  emptyFilterStateSlot,
  type CompanyFilterValue,
  type FilterStateSlot,
} from './rates-filter-state.service';

/** sessionStorage key. Bumping SNAPSHOT_VERSION is what invalidates old data;
 *  the key itself should stay put so we don't leak orphaned entries. */
export const RATES_FILTER_STORAGE_KEY = 'bn.rates.filterState';

/** Bump when a stored snapshot can no longer be read safely — e.g. a
 *  FilterStateSlot field, or one of its values, changes meaning rather than
 *  merely being added. A field that is only *added* does not need a bump:
 *  sanitizeSlot() fills it from emptyFilterStateSlot().
 *
 *  2 (2026-09-22): the 'Periodic' premium type was relabelled 'Both'. A
 *  restored 'Periodic' pick would match no row and present as an unexplained
 *  empty grid, so v1 snapshots are discarded rather than migrated. */
export const RATES_FILTER_SNAPSHOT_VERSION = 2;

/** Every ProductType the service keys its Records by. */
const ALL_TABS: readonly ProductType[] = ['FA', 'IA', 'RILA', 'VA', 'ALL'] as const;

/** FA-only filter state — standalone signals on the service, not slot fields. */
export interface FaFilterSnapshot {
  rateTerms: number[];
  surrYears: number[];
  bonusYears: number[];
  mva: boolean;
  qualified: boolean;
}

export interface RatesFilterSnapshot {
  v: number;
  activeTab: ProductType;
  slots: Record<ProductType, FilterStateSlot>;
  company: Record<ProductType, CompanyFilterValue | null>;
  fa: FaFilterSnapshot;
  floatChanges: boolean;
}

export function emptyFaFilterSnapshot(): FaFilterSnapshot {
  return { rateTerms: [], surrYears: [], bonusYears: [], mva: false, qualified: false };
}

// ── Field coercion ─────────────────────────────────────────────────────

const isTab = (v: unknown): v is ProductType =>
  typeof v === 'string' && (ALL_TABS as readonly string[]).includes(v);

const strArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const numArray = (v: unknown): number[] =>
  Array.isArray(v) ? v.filter((x): x is number => typeof x === 'number' && Number.isFinite(x)) : [];

const bool = (v: unknown): boolean => v === true;

/** Numeric slot fields are nullable — `0` is a legitimate value, so an
 *  `?? null` on a falsy check would be wrong. */
const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const strOrNull = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * Rebuild one slot from untrusted JSON. Starts from a fresh empty slot so any
 * field the stored snapshot lacks (older build, hand-edited storage) is present
 * and correctly typed — consumers read `.length` / `.includes()` on these
 * without guarding.
 */
export function sanitizeSlot(raw: unknown): FilterStateSlot {
  const slot = emptyFilterStateSlot();
  if (!raw || typeof raw !== 'object') return slot;
  const r = raw as Record<string, unknown>;
  slot.selectedStates = strArray(r['selectedStates']);
  slot.selectedIndexes = strArray(r['selectedIndexes']);
  slot.selectedSurrYears = strArray(r['selectedSurrYears']);
  slot.selectedCreditingStrategies = strArray(r['selectedCreditingStrategies']);
  slot.selectedStrategyNames = strArray(r['selectedStrategyNames']);
  slot.selectedStrategyCombos = strArray(r['selectedStrategyCombos']);
  slot.contributionAmount = numOrNull(r['contributionAmount']);
  slot.filterRop = bool(r['filterRop']);
  slot.filterBailOut = bool(r['filterBailOut']);
  slot.selectedAmBest = strArray(r['selectedAmBest']);
  slot.filterBonus = bool(r['filterBonus']);
  slot.filterInverse = bool(r['filterInverse']);
  slot.selectedRiders = strArray(r['selectedRiders']);
  slot.selectedPremiumTypes = strArray(r['selectedPremiumTypes']);
  slot.filterOwner = bool(r['filterOwner']);
  slot.filterAnnuitant = bool(r['filterAnnuitant']);
  slot.selectedBufferLevels = strArray(r['selectedBufferLevels']);
  slot.selectedFloorLevels = strArray(r['selectedFloorLevels']);
  slot.selectedSegmentDurations = strArray(r['selectedSegmentDurations']);
  slot.selectedRateTypes = strArray(r['selectedRateTypes']);
  slot.selectedProtectionType = strOrNull(r['selectedProtectionType']);
  slot.minPremiumMin = numOrNull(r['minPremiumMin']);
  slot.minPremiumMax = numOrNull(r['minPremiumMax']);
  slot.searchText = typeof r['searchText'] === 'string' ? r['searchText'] : '';
  return slot;
}

/** A carrier selection is only meaningful with both arrays; anything else
 *  becomes null ("no carrier filter") rather than a half-applied filter that
 *  would hide every row. */
function sanitizeCompany(raw: unknown): CompanyFilterValue | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r['companies'])) return null;
  return { companies: strArray(r['companies']), products: strArray(r['products']) };
}

function sanitizeFa(raw: unknown): FaFilterSnapshot {
  if (!raw || typeof raw !== 'object') return emptyFaFilterSnapshot();
  const r = raw as Record<string, unknown>;
  return {
    rateTerms: numArray(r['rateTerms']),
    surrYears: numArray(r['surrYears']),
    bonusYears: numArray(r['bonusYears']),
    mva: bool(r['mva']),
    qualified: bool(r['qualified']),
  };
}

// ── Serialize / parse ──────────────────────────────────────────────────

export function buildRatesFilterSnapshot(input: {
  activeTab: ProductType;
  slots: Record<ProductType, FilterStateSlot>;
  company: Record<ProductType, CompanyFilterValue | null>;
  fa: FaFilterSnapshot;
  floatChanges: boolean;
}): RatesFilterSnapshot {
  return { v: RATES_FILTER_SNAPSHOT_VERSION, ...input };
}

/**
 * Parse a stored snapshot. Returns null for absent, malformed, or
 * version-mismatched data — callers keep their in-memory defaults.
 */
export function parseRatesFilterSnapshot(raw: string | null): RatesFilterSnapshot | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const p = parsed as Record<string, unknown>;
  if (p['v'] !== RATES_FILTER_SNAPSHOT_VERSION) return null;

  const slotsRaw = (p['slots'] ?? {}) as Record<string, unknown>;
  const companyRaw = (p['company'] ?? {}) as Record<string, unknown>;
  const slots = {} as Record<ProductType, FilterStateSlot>;
  const company = {} as Record<ProductType, CompanyFilterValue | null>;
  for (const t of ALL_TABS) {
    slots[t] = sanitizeSlot(slotsRaw[t]);
    company[t] = sanitizeCompany(companyRaw[t]);
  }

  return {
    v: RATES_FILTER_SNAPSHOT_VERSION,
    activeTab: isTab(p['activeTab']) ? p['activeTab'] : 'IA',
    slots,
    company,
    fa: sanitizeFa(p['fa']),
    floatChanges: bool(p['floatChanges']),
  };
}

// ── Storage access ─────────────────────────────────────────────────────
//
// Every call is wrapped: sessionStorage throws on access in a private window
// with site data blocked, and is absent entirely under SSR/unit tests. A
// storage failure must never take the rates page down — it just means filters
// don't survive the next refresh.

export function readRatesFilterSnapshot(): RatesFilterSnapshot | null {
  try {
    return parseRatesFilterSnapshot(sessionStorage.getItem(RATES_FILTER_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function writeRatesFilterSnapshot(snapshot: RatesFilterSnapshot): void {
  try {
    sessionStorage.setItem(RATES_FILTER_STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* storage unavailable or quota exceeded — persistence is best-effort */
  }
}
